import { Chakra } from "@airhelp/react-ui-components";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import getPageUrl from "utils/getPageUrl";
import config from "config/config";
import AirHelpScoreWrapper from "../AirHelpScoreWrapper";
import type { AirlineRankingProps } from "./AirlineRanking.d";
import {
  Hero,
  CTA,
  TopThree,
  HowScoreIsCalculated,
  RelatedArticles,
  ScoreTag,
  BestAirlines,
  Share,
  JoinTheCommunity,
  About,
} from "../common";
import TopThreeCard from "../common/TopThree/TopThreeCard";
import AirlineRankingTable from "./AirlineRankingTable";
import columns from "./AirlineRankingTable/config";
import { HOW_SCORE_IS_CALCULATED_AIRLINE_ITEMS, AIRLINES_METHODOLOGY_PDF_LINK, AIRPORT_POST_IMAGE } from "./consts";

const { Box, Image, Heading } = Chakra;

const AirlineRanking = ({
  story,
  cmsLanguagesData,
  serverSideStoryblokPages,
  airlinesScores,
  airlinesScoresByCountry,
  relatedArticles,
}: AirlineRankingProps) => {
  const translations = useTranslations();
  const language = useLanguage();
  const topThreeAirlines = airlinesScores.slice(0, 3);

  return (
    <AirHelpScoreWrapper
      story={story}
      cmsLanguagesData={cmsLanguagesData}
      serverSideStoryblokPages={serverSideStoryblokPages}
    >
      <Hero
        heading={translations("global_airline_ranking")}
        subHeading={translations("airhelp_score_2019")}
        text={translations("see_how_the_worlds_airlines_compare")}
      />
      <Box mt={{ base: 12, lg: 20 }} bgGradient="linear(to-b, greyscale.100 50%, greyscale.300 0)">
        <CTA
          text={translations("how_do_i_know")}
          actionButtonText={translations("check_compensation")}
          actionButtonHref={getPageUrl(language, "webappNewClaim")}
          checkboxes={[translations("all_airlines"), translations("all_countries"), translations("no_win_no_fee")]}
        />
      </Box>
      <TopThree heading={translations("best_airlines_pax_exp")}>
        {topThreeAirlines.map((item, index) => {
          const { airline, airline_code: airlineCode, score } = item;

          return (
            <TopThreeCard key={airlineCode}>
              <Image
                src={`${config.staticUrl}/i/airhelp-score/airlines/${airlineCode.toLowerCase()}.png`}
                alt={airline}
                margin="auto"
                maxH={14}
                mb={6}
              />
              <Heading variant="h3" as="h3" mb={4}>
                {airline}
              </Heading>
              <ScoreTag index={index} score={score} size="lg" />
            </TopThreeCard>
          );
        })}
      </TopThree>
      <AirlineRankingTable columns={columns} rows={airlinesScores} heading="global_airline_ranking" />
      <BestAirlines airlinesScoresByCountry={airlinesScoresByCountry} />
      <HowScoreIsCalculated
        items={HOW_SCORE_IS_CALCULATED_AIRLINE_ITEMS}
        learnMoreLink={AIRLINES_METHODOLOGY_PDF_LINK}
      />
      <RelatedArticles relatedArticles={relatedArticles} fallbackFeaturedImage={AIRPORT_POST_IMAGE} />
      <About />
      <JoinTheCommunity />
      <Share />
    </AirHelpScoreWrapper>
  );
};

export default AirlineRanking;
